if (!customElements.get('convercy-currency-form')) {
  customElements.define(
    'convercy-currency-form',
    class ConvercyCurrencyForm extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector('.header-wrapper');
        this.storageKey = 'cvc_switcher';
        this.elements = {
          button: this.querySelector('button.localization-form__select'),
          buttonLabel: this.querySelector('.convercy-currency__button-label'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          closeButton: this.querySelector('.country-selector__close-button'),
        };

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }

        this.querySelectorAll('a[data-value]').forEach((item) =>
          item.addEventListener('click', this.onItemClick.bind(this))
        );

        this.syncFromStorage();
        this.syncWhenConvercyReady();
      }

      getStoredCurrency() {
        try {
          const raw = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
          if (raw && raw.value && raw.value.currency && new Date(raw.expire) > new Date()) {
            return raw.value.currency;
          }
        } catch (error) {
          // Convercy storage is missing or invalid; fall back to the default currency.
        }

        if (window._convercy && window._convercy.currencyCurrent && window._convercy.currencyCurrent.code) {
          return window._convercy.currencyCurrent.code;
        }

        return this.dataset.defaultCurrency || 'AED';
      }

      syncFromStorage() {
        const code = this.getStoredCurrency();
        const item = this.querySelector(`a[data-value="${code}"]`);
        if (!item) return;
        this.updateSelectedUI(item, true);
      }

      syncWhenConvercyReady() {
        if (window._convercy && window._convercy.currencyCurrent) {
          this.syncFromStorage();
          return;
        }

        const startedAt = Date.now();
        const timer = setInterval(() => {
          const ready = window._convercy && window._convercy.currencyCurrent;
          if (ready || Date.now() - startedAt > 4000) {
            clearInterval(timer);
            this.syncFromStorage();
          }
        }, 200);
      }

      formatCurrencyText(code, symbol, label, showName) {
        const isArabic = this.dataset.isArabic === 'true';

        // Arabic locale keeps the original CODE (symbol) display
        if (isArabic) {
          return showName ? `${label} (${code} ${symbol})` : `${code} ${symbol}`;
        }

        // EN: Latin currency codes only
        return showName ? `${label} (${code})` : code;
      }

      updateSelectedUI(item, updateButton = true) {
        this.querySelectorAll('a[data-value]').forEach((link) => link.removeAttribute('aria-current'));
        item.setAttribute('aria-current', 'true');

        if (!updateButton || !this.elements.buttonLabel) return;

        const showName = this.dataset.showName === 'true';
        const label = item.dataset.label;
        const code = item.dataset.value;
        const symbol = item.dataset.symbol;
        const text = this.formatCurrencyText(code, symbol, label, showName);

        this.elements.buttonLabel.innerHTML = `${this.buildFlagHtml(item)}${text}`;
      }

      buildFlagHtml(item) {
        const flagImg = item.querySelector('img');
        if (!flagImg) return '';

        const clone = flagImg.cloneNode(true);
        clone.classList.add('mr-4');
        clone.removeAttribute('srcset');
        clone.removeAttribute('sizes');
        clone.src = flagImg.getAttribute('src') || flagImg.currentSrc || flagImg.src;
        return clone.outerHTML;
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        document.body.classList.remove('overflow-hidden-mobile');
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer) menuDrawer.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a[data-value]')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }
      }

      onContainerKeyUp(event) {
        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            event.preventDefault();
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const item = event.currentTarget;
        const code = item.dataset.value;
        if (!code) return;

        this.updateSelectedUI(item, true);
        this.hidePanel();
        this.switchConvercyCurrency(code);
      }

      switchConvercyCurrency(code) {
        if (this.clickConvercyItem(code)) return;

        const startedAt = Date.now();
        const timer = setInterval(() => {
          if (this.clickConvercyItem(code)) {
            clearInterval(timer);
            return;
          }

          if (Date.now() - startedAt > 8000) {
            clearInterval(timer);
            this.persistAndReload(code);
          }
        }, 200);
      }

      clickConvercyItem(code) {
        const items = Array.from(document.querySelectorAll(`.cvc-list-item[cvc-code="${code}"]`));
        const currencyItem =
          items.find(
            (el) =>
              (el.getAttribute('aria-label') || '').toLowerCase().includes('currency') && !el.closest('.cvc-geo')
          ) ||
          items.find((el) => (el.getAttribute('aria-label') || '').toLowerCase().includes('currency'));

        if (currencyItem) {
          currencyItem.click();
          return true;
        }

        const switcher = window._convercy && window._convercy.switchers && window._convercy.switchers[0];
        const expand = switcher && (switcher.expandEl || switcher.expand);
        const fromSwitcher = expand && expand.querySelector(`.cvc-list-item[cvc-code="${code}"]`);

        if (fromSwitcher) {
          fromSwitcher.click();
          return true;
        }

        return false;
      }

      persistAndReload(code) {
        let previous = {};

        try {
          const raw = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
          if (raw && raw.value) previous = raw.value;
        } catch (error) {
          previous = {};
        }

        const value = {
          locale: previous.locale || '',
          currency: code,
          country: previous.country || (window.Shopify && window.Shopify.country) || '',
          times: previous.times || 0,
        };
        const expire = new Date(Date.now() + 43200 * 60 * 1000).toISOString();

        try {
          localStorage.setItem(this.storageKey, JSON.stringify({ value, expire }));
        } catch (error) {
          return;
        }

        window.location.reload();
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer) menuDrawer.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }
    }
  );
}
